import React, { useState, useEffect, useCallback } from 'react';
import {
    View,
    Text,
    StyleSheet,
    Switch,
    TouchableOpacity,
} from 'react-native';
import { SafeAreaView } from 'react-native-safe-area-context';
import { KeyboardAwareScrollView } from 'react-native-keyboard-controller';
import { useForm, Controller } from 'react-hook-form';
import { CustomInput } from '../components/CustomInput';
import { DatePickerInput } from '../components/DatePickerInput';
import { AppModal, ModalType } from '../components/AppModal';
import { LoadingOverlay } from '../components/LoadingOverlay';
import { theme } from '../constants/theme';
import ApiService from '../services/ApiService';
import { Cow } from '../models/types';
import { numericValidation, alphabetOnlyNoSpace } from '../utils/helpers';
import { ScreenHeader } from '../components/ScreenHeader';

interface CowFormData {
    id: string;
    name: string;
    breed: string;
    dateOfBirth: string;
    purchaseDate: string;
    purchasePrice: string;
    isPregnant: boolean;
    pregnancyDate: string;
    expectedCalvingDate: string;
    notes: string;
}

const AddEditCowScreen = ({ navigation, route }: any) => {
    const existingCow = route.params?.cow as Cow | undefined;
    const [loading, setLoading] = useState(false);
    const [modal, setModal] = useState<{
        visible: boolean;
        type: ModalType;
        title: string;
        message: string;
        isSuccess?: boolean;
    }>({ visible: false, type: 'info', title: '', message: '' });

    const {
        control,
        handleSubmit,
        setValue,
        watch,
        formState: { errors },
    } = useForm<CowFormData>({
        defaultValues: {
            id: '',
            name: '',
            breed: '',
            dateOfBirth: '',
            purchaseDate: '',
            purchasePrice: '',
            isPregnant: false,
            pregnancyDate: '',
            expectedCalvingDate: '',
            notes: '',
        },
        mode: 'onBlur',
    });

    const isPregnant = watch('isPregnant');
    const expectedCalvingDate = watch('expectedCalvingDate');

    // Calculate expected calving date (approximately 283 days from pregnancy date)
    const calculateExpectedCalvingDate = (pregnancyDate: string): string => {
        if (!pregnancyDate) return '';
        const date = new Date(pregnancyDate);
        date.setDate(date.getDate() + 283);
        return date.toISOString().split('T')[0];
    };

    useEffect(() => {
        const initializeForm = async () => {
            if (existingCow) {
                setValue('id', existingCow.id);
                setValue('name', existingCow.name || '');
                setValue('breed', existingCow.breed || '');
                setValue('dateOfBirth', existingCow.dateOfBirth || '');
                setValue('purchaseDate', existingCow.purchaseDate || '');
                setValue('purchasePrice', existingCow.purchasePrice?.toString() || '');
                setValue('isPregnant', existingCow.isPregnant || false);
                setValue('pregnancyDate', existingCow.pregnancyDate || '');
                setValue('expectedCalvingDate', existingCow.expectedCalvingDate || '');
                setValue('notes', existingCow.notes || '');
            } else {
                const nextId = await ApiService.getNextCowId();
                setValue('id', nextId);
            }
        };

        initializeForm();
    }, [existingCow, setValue]);

    const handlePregnancyDateChange = (date: string) => {
        const expectedDate = calculateExpectedCalvingDate(date);
        setValue('pregnancyDate', date);
        setValue('expectedCalvingDate', expectedDate);
    };

    const handlePregnancyToggle = (value: boolean) => {
        setValue('isPregnant', value);
        if (!value) {
            setValue('pregnancyDate', '');
            setValue('expectedCalvingDate', '');
        }
    };

    const handleModalClose = () => {
        const isSuccess = modal.isSuccess;
        setModal(prev => ({ ...prev, visible: false }));
        if (isSuccess) {
            navigation.goBack();
        }
    };

    const onSubmit = useCallback(async (data: CowFormData) => {
        setLoading(true);
        try {
            const now = new Date().toISOString();
            const cowData: Cow = {
                id: data.id.trim(),
                name: data.name.trim() || undefined,
                breed: data.breed.trim() || undefined,
                dateOfBirth: data.dateOfBirth.trim() || undefined,
                purchaseDate: data.purchaseDate.trim() || undefined,
                purchasePrice: data.purchasePrice
                    ? parseFloat(data.purchasePrice)
                    : undefined,
                isPregnant: data.isPregnant,
                pregnancyDate: data.pregnancyDate.trim() || undefined,
                expectedCalvingDate: data.expectedCalvingDate.trim() || undefined,
                notes: data.notes.trim() || undefined,
                currentStatus: existingCow?.currentStatus || 'active',
                createdAt: existingCow?.createdAt || now,
                updatedAt: now,
            };

            if (existingCow) {
                await ApiService.updateCow(cowData.id, cowData);
                setModal({
                    visible: true,
                    type: 'success',
                    title: 'Success',
                    message: 'Cow updated successfully',
                    isSuccess: true,
                });
            } else {
                await ApiService.addCow(cowData);
                setModal({
                    visible: true,
                    type: 'success',
                    title: 'Success',
                    message: 'Cow added successfully',
                    isSuccess: true,
                });
            }
        } catch (error) {
            console.error('Error saving cow:', error);
            setModal({
                visible: true,
                type: 'error',
                title: 'Error',
                message: 'Failed to save cow. Please try again.',
            });
        } finally {
            setLoading(false);
        }
    }, [existingCow]);

    return (
        <SafeAreaView style={styles.container}>
            <ScreenHeader
                title={existingCow ? 'Edit Cow' : 'Add New Cow'}
                navigation={navigation}
                showBackButton={true}
            />
            <KeyboardAwareScrollView
                style={styles.scrollView}
                contentContainerStyle={styles.contentContainer}
                showsVerticalScrollIndicator={false}
                bottomOffset={20}
            >
                <View style={styles.form}>
                    <Controller
                        control={control}
                        name="id"
                        rules={{ required: 'Cow ID is required' }}
                        render={({ field: { onChange, onBlur, value } }) => (
                            <CustomInput
                                label="Cow ID *"
                                value={value}
                                onChangeText={(text) => onChange(text)}
                                onBlur={onBlur}
                                icon="identifier"
                                error={errors.id?.message}
                                editable={false}
                                placeholder="e.g., COW001"
                            />
                        )}
                    />

                    <Controller
                        control={control}
                        name="name"
                        render={({ field: { onChange, onBlur, value } }) => (
                            <CustomInput
                                label="Name (Optional)"
                                value={value}
                                onChangeText={(text) => onChange(alphabetOnlyNoSpace(text))}
                                onBlur={onBlur}
                                icon="cow"
                                placeholder="e.g., Lakshmi"
                            />
                        )}
                    />

                    <Controller
                        control={control}
                        name="breed"
                        rules={{ required: 'Breed is required' }}
                        render={({ field: { onChange, onBlur, value } }) => (
                            <CustomInput
                                label="Breed *"
                                value={value}
                                onChangeText={(text) => onChange(alphabetOnlyNoSpace(text))}
                                onBlur={onBlur}
                                icon="tag"
                                error={errors.breed?.message}
                                placeholder="e.g., HolsteinFriesian"
                            />
                        )}
                    />

                    <Controller
                        control={control}
                        name="dateOfBirth"
                        render={({ field: { onChange, value } }) => (
                            <DatePickerInput
                                label="Date of Birth (Optional)"
                                value={value}
                                onChangeDate={onChange}
                                placeholder="Select date of birth"
                                maximumDate={new Date()}
                            />
                        )}
                    />

                    <Controller
                        control={control}
                        name="purchaseDate"
                        rules={{ required: 'Purchase date is required' }}
                        render={({ field: { onChange, value } }) => (
                            <DatePickerInput
                                label="Purchase Date *"
                                value={value}
                                onChangeDate={onChange}
                                error={errors.purchaseDate?.message}
                                placeholder="Select purchase date"
                                maximumDate={new Date()}
                            />
                        )}
                    />

                    <Controller
                        control={control}
                        name="purchasePrice"
                        rules={{ required: 'Purchase price is required' }}
                        render={({ field: { onChange, onBlur, value } }) => (
                            <CustomInput
                                label="Purchase Price *"
                                value={value}
                                onChangeText={(text) => onChange(numericValidation(text))}
                                onBlur={onBlur}
                                icon="currency-inr"
                                error={errors.purchasePrice?.message}
                                placeholder="e.g., 50000"
                                keyboardType="number-pad"
                            />
                        )}
                    />

                    {/* Pregnancy Section */}
                    <View style={styles.pregnancySection}>
                        <Text style={styles.sectionTitle}>Pregnancy Information</Text>

                        <View style={styles.switchRow}>
                            <Text style={styles.switchLabel}>Is Pregnant?</Text>
                            <Controller
                                control={control}
                                name="isPregnant"
                                render={({ field: { value } }) => (
                                    <Switch
                                        value={value}
                                        onValueChange={handlePregnancyToggle}
                                        trackColor={{ false: theme.colors.border, true: theme.colors.primaryLight }}
                                        thumbColor={value ? theme.colors.primary : theme.colors.textLight}
                                    />
                                )}
                            />
                        </View>

                        {isPregnant && (
                            <>
                                <Controller
                                    control={control}
                                    name="pregnancyDate"
                                    render={({ field: { value } }) => (
                                        <DatePickerInput
                                            label="Pregnancy Date"
                                            value={value}
                                            onChangeDate={handlePregnancyDateChange}
                                            placeholder="When did the cow become pregnant?"
                                            maximumDate={new Date()}
                                        />
                                    )}
                                />

                                <Controller
                                    control={control}
                                    name="expectedCalvingDate"
                                    render={({ field: { onChange, value } }) => (
                                        <DatePickerInput
                                            label="Expected Calving Date"
                                            value={value}
                                            onChangeDate={onChange}
                                            placeholder="Expected delivery date"
                                            minimumDate={new Date()}
                                        />
                                    )}
                                />

                                {expectedCalvingDate && (
                                    <View style={styles.calvingInfo}>
                                        <Text style={styles.calvingInfoText}>
                                            📅 Expected calving: {new Date(expectedCalvingDate).toLocaleDateString('en-US', {
                                                month: 'long',
                                                day: 'numeric',
                                                year: 'numeric'
                                            })}
                                        </Text>
                                    </View>
                                )}
                            </>
                        )}
                    </View>

                    <Controller
                        control={control}
                        name="notes"
                        render={({ field: { onChange, onBlur, value } }) => (
                            <CustomInput
                                label="Notes (Optional)"
                                value={value}
                                onChangeText={onChange}
                                onBlur={onBlur}
                                icon="note-text"
                                placeholder="Any additional information..."
                                multiline
                                numberOfLines={4}
                            />
                        )}
                    />

                    {/* Action Buttons */}
                    <View style={styles.buttonRow}>
                        <TouchableOpacity
                            style={styles.saveBtn}
                            onPress={handleSubmit(onSubmit)}
                            activeOpacity={0.7}
                            disabled={loading}
                        >
                            <Text style={styles.saveBtnText}>
                                {loading ? 'Saving...' : (existingCow ? 'Update Cow' : 'Add Cow')}
                            </Text>
                        </TouchableOpacity>

                        <TouchableOpacity
                            style={styles.cancelBtn}
                            onPress={() => navigation.goBack()}
                            activeOpacity={0.7}
                        >
                            <Text style={styles.cancelBtnText}>Cancel</Text>
                        </TouchableOpacity>
                    </View>
                </View>
            </KeyboardAwareScrollView>

            <AppModal
                visible={modal.visible}
                type={modal.type}
                title={modal.title}
                message={modal.message}
                onClose={handleModalClose}
            />

            <LoadingOverlay visible={loading} message="Saving..." />
        </SafeAreaView>
    );
};

const styles = StyleSheet.create({
    container: {
        flex: 1,
        backgroundColor: theme.colors.background,
    },
    scrollView: {
        flex: 1,
    },
    contentContainer: {
        flexGrow: 1,
    },
    form: {
        padding: theme.spacing.lg,
    },
    pregnancySection: {
        backgroundColor: theme.colors.surface,
        borderRadius: theme.borderRadius.lg,
        padding: theme.spacing.md,
        marginBottom: theme.spacing.md,
        ...theme.shadows.sm,
    },
    sectionTitle: {
        ...theme.typography.h6,
        color: theme.colors.text,
        marginBottom: theme.spacing.md,
        fontWeight: '600',
    },
    switchRow: {
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: theme.spacing.md,
    },
    switchLabel: {
        ...theme.typography.body1,
        color: theme.colors.text,
        fontWeight: '500',
    },
    calvingInfo: {
        backgroundColor: theme.colors.primaryLight + '20',
        borderRadius: theme.borderRadius.md,
        padding: theme.spacing.md,
        marginTop: theme.spacing.sm,
    },
    calvingInfoText: {
        ...theme.typography.body2,
        color: theme.colors.primary,
        fontWeight: '600',
    },
    buttonRow: {
        flexDirection: 'row',
        gap: theme.spacing.md,
        marginTop: theme.spacing.md,
    },
    saveBtn: {
        flex: 2,
        alignItems: 'center',
        justifyContent: 'center',
        // paddingVertical: theme.spacing.md,
        height: 45,
        borderRadius: theme.borderRadius.lg,
        backgroundColor: theme.colors.primary,
        ...theme.shadows.sm,
    },
    saveBtnText: {
        ...theme.typography.button,
        color: '#FFFFFF',
        fontWeight: '700',
    },
    cancelBtn: {
        flex: 1,
        alignItems: 'center',
        justifyContent: 'center',
        height: 45,
        borderRadius: theme.borderRadius.lg,
        backgroundColor: theme.colors.surface,
        borderWidth: 1,
        borderColor: theme.colors.border,
    },
    cancelBtnText: {
        ...theme.typography.button,
        color: theme.colors.textSecondary,
        fontWeight: '600',
    },
});

export default AddEditCowScreen;
